import { createContext } from 'react'

import type {
  TabsColor,
  TabsCornerState,
  TabsIndicator,
  TabsOrientation,
  TabsPaperLevel,
  TabsSize,
} from './tabs.styles'

/** Single value carried by a tab — strings or numbers, or `null` for "no active tab" */
export type TabsValue = string | number | null

/** Corner shape for the Panel's top corners — driven by active-tab alignment. */
export type TopCorners = {
  topLeft: TabsCornerState
  topRight: TabsCornerState
}

/**
 * Context shared by `Root` with its children so every `Tab` / `List` / `Panel`
 * knows the `color`, `fullWidth`, `scrollable`, `orientation` and `paperLevel`
 * settings chosen at the root level without prop-drilling.
 *
 * `activeValue` mirrors base-ui's controlled / uncontrolled value so `Panel`
 * has a useEffect dep that fires when the active tab changes — Panel measures
 * the active tab's horizontal edges and squares the corners that align with
 * its own (the "moustache" lands on a square corner; misaligned corners stay
 * rounded). See `panel.tsx` for the measurement logic.
 *
 * `corners` and `setCorners` live in Root so the SHARED corner state survives
 * a Panel-instance swap (non-keepMounted, the new active Panel mounts with
 * empty React state; without shared corners it would briefly flash
 * `BOTH_ROUNDED` before the deferred measurement commits, producing a visible
 * mid-slide corner snap).
 */
export type TabsContextValue = {
  color: TabsColor
  size: TabsSize
  fullWidth: boolean
  /**
   * Straight from Root's prop. `List` renders the scroll layout for any truthy
   * value; for `'auto'` it hides the chevrons while the content fits.
   */
  scrollable: boolean | 'auto'
  detached: boolean
  indicator: TabsIndicator
  orientation: TabsOrientation
  paperLevel: TabsPaperLevel
  activeValue: TabsValue
  corners: TopCorners
  setCorners: (next: TopCorners) => void
}

const BOTH_ROUNDED: TopCorners = { topLeft: 'round', topRight: 'round' }

export const TabsContext = createContext<TabsContextValue>({
  color: 'primary',
  size: 'medium',
  fullWidth: false,
  scrollable: false,
  detached: false,
  indicator: 'pill',
  orientation: 'horizontal',
  paperLevel: 3,
  activeValue: null,
  corners: BOTH_ROUNDED,
  setCorners: () => undefined,
})
